/**
 * PublishSuccessFlow — ujednolicony Ekran Sukcesu po publikacji dla WSZYSTKICH bytów
 * (suka / reproduktor / miot — oraz docelowo hodowla).
 *
 * Graficznie identyczny we wszystkich kreatorach (ta sama ciemna szklana karta, te same
 * przyciski udostępniania, ta sama lista korzyści i sekcja „Co dalej?"). Różni się wyłącznie
 * nagłówkami (entityType), publicznym linkiem oraz listą kroków „Co dalej?" (nextItems).
 *
 * Zasada produktu: każdy byt może istnieć samodzielnie jako landing → ekran sukcesu musi
 * obiecywać to samo i wyglądać tak samo.
 */
import { useState } from 'react';
import {
  ArrowRight, X, Copy, Facebook, MessageCircle, Globe, CheckCircle2, LayoutGrid,
  ClipboardCheck, ThumbsUp, MessageSquare, Share2,
} from 'lucide-react';
import { toast } from 'sonner';

const FLOW_CONFIG = {
  kennel: {
    publicPrefix: 'hodowla',
    catalogHref: '/katalog?type=hodowle',
    badge: 'Twoja strona jest już online',
    publishedText: 'Twoja hodowla została opublikowana i jest już dostępna online.',
    shareText: 'Zobacz moją hodowlę: ',
    checklist: ['Hodowla widoczna w APISAI', 'Dodana do katalogu', 'Gotowa do przyjmowania zapytań'],
  },
  suka: {
    publicPrefix: 'pies',
    catalogHref: '/katalog?type=hodowle',
    badge: 'Profil suki jest już online',
    publishedText: 'Profil suki został opublikowany i ma własny landing — możesz go promować osobno.',
    shareText: 'Zobacz: ',
    checklist: ['Profil widoczny w APISAI', 'Dodany do katalogu', 'Gotowy do przyjmowania zapytań'],
  },
  reproduktor: {
    publicPrefix: 'reproduktor',
    catalogHref: '/katalog?type=reproduktory',
    badge: 'Profil reproduktora jest już online',
    publishedText: 'Profil reproduktora został opublikowany i ma własny landing krycia — możesz go promować osobno.',
    shareText: 'Reproduktor do krycia: ',
    checklist: ['Profil widoczny w APISAI', 'Dodany do katalogu', 'Gotowy do przyjmowania zapytań'],
  },
  miot: {
    publicPrefix: 'miot',
    catalogHref: '/katalog?type=mioty',
    badge: 'Profil miotu jest już online',
    publishedText: 'Profil miotu został opublikowany i ma własny landing — możesz go promować osobno.',
    shareText: 'Szczeniaki do odbioru: ',
    checklist: ['Profil widoczny w APISAI', 'Dodany do katalogu', 'Gotowy do przyjmowania zapytań'],
  },
  firma: {
    publicPrefix: 'firma',
    catalogHref: '/katalog/uslugi',
    badge: 'Twoja strona jest już online',
    publishedText: 'Profil firmy został opublikowany i jest już dostępny online.',
    shareText: 'Zobacz moją firmę: ',
    checklist: ['Firma widoczna w APISAI', 'Dodana do katalogu', 'Gotowa do przyjmowania zapytań'],
  },
};

/* Gotowy do wklejenia post na Facebooka — per typ bytu. Facebook nie pozwala wstępnie
   wypełnić treści posta z poziomu linku, dlatego dajemy „Kopiuj treść posta" + „Otwórz Facebooka".
   Sam link doda podgląd (grafikę + tytuł) automatycznie po wklejeniu. */
function buildFacebookPost(entityType, { name, subtitle, url }) {
  const n = (name || '').trim();
  const sub = (subtitle || '').trim() ? ` (${subtitle.trim()})` : '';
  const link = url || '';
  switch (entityType) {
    case 'suka':
      return `✨ Poznajcie naszą sukę${n ? ` ${n}` : ''}${sub}!\n\nZdjęcia, rodowód i badania znajdziecie na jej nowej stronie. 🐾\n\n👉 ${link}`;
    case 'reproduktor':
      return `🏆 Nasz reproduktor${n ? ` ${n}` : ''}${sub} jest dostępny do krycia!\n\nSprawdźcie rodowód, badania zdrowotne i warunki krycia na jego stronie. 🐾\n\n👉 ${link}`;
    case 'miot':
      return `🐶 Szczeniaki${n ? ` ${n}` : ''}${sub} już są!\n\nDostępne do rezerwacji — zobaczcie zdjęcia, rodziców i szczegóły na stronie miotu. ❤️\n\n👉 ${link}`;
    case 'firma':
      return `🎉 ${n || 'Nasza firma'} jest już online!\n\nZobaczcie naszą ofertę i napiszcie do nas — chętnie pomożemy. 👇\n\n👉 ${link}`;
    case 'kennel':
    default:
      return `🐶 Nasza hodowla${n ? ` ${n}` : ''}${sub} jest już online!\n\nZapraszamy do zapoznania się z naszymi psami i ofertą. Jeśli szukacie zdrowego szczeniaka z odpowiedzialnej hodowli — napiszcie do nas. ❤️\n\n👉 ${link}`;
  }
}

function Backdrop({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/55 backdrop-blur-[3px]"
      onClick={onClose}
      aria-hidden="true"
      data-testid="publish-success-backdrop"
    />
  );
}

function Shell({ children }) {
  return (
    <div className="fixed z-[60] inset-0 flex items-center justify-center p-4 pointer-events-none" role="dialog" aria-modal="true">
      <div
        className="relative pointer-events-auto w-full max-w-md max-h-[92vh] overflow-y-auto rounded-3xl ring-1 ring-white/8 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.85)]"
        style={{ background: 'linear-gradient(160deg, #04202F 0%, #052B3D 60%, #073447 100%)' }}
      >
        <div
          className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-45"
          style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.55), transparent 65%)' }}
          aria-hidden="true"
        />
        {children}
      </div>
    </div>
  );
}

export default function PublishSuccessFlow({
  entityType = 'kennel',
  slug,
  userName,
  entityName,
  coverImage,
  subtitle,
  onClose,
  onNavigate,
  nextItems = [],
}) {
  const cfg = FLOW_CONFIG[entityType] || FLOW_CONFIG.kennel;
  const firstName = userName?.split(' ')[0] || 'Aniu';
  const pageUrl = slug ? `${window.location.origin}/${cfg.publicPrefix}/${slug}` : '';
  const [copied, setCopied] = useState(false);
  const postText = buildFacebookPost(entityType, { name: entityName, subtitle, url: pageUrl });

  const copyLink = () => {
    if (!pageUrl) return;
    navigator.clipboard?.writeText(pageUrl);
    toast.success('Link skopiowany — wklej go na Facebooku lub w Messengerze.');
  };
  const copyPost = () => {
    if (!pageUrl) return;
    navigator.clipboard?.writeText(postText);
    setCopied(true);
    toast.success('Treść posta skopiowana — wklej ją na Facebooku.');
    setTimeout(() => setCopied(false), 2500);
  };
  const shareFacebook = () => {
    if (!pageUrl) return;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`, '_blank', 'noopener,noreferrer,width=640,height=600');
  };
  const shareWhatsApp = () => {
    if (!pageUrl) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(cfg.shareText + pageUrl)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Backdrop onClose={onClose} />
      <Shell>
        <button
          onClick={onClose}
          data-testid={`${entityType}-success-dismiss`}
          className="absolute top-4 right-4 w-8 h-8 rounded-lg flex items-center justify-center text-white/45 hover:text-white hover:bg-white/8 transition-colors z-10"
          aria-label="Zamknij"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative p-7 sm:p-9 space-y-5" data-testid={`${entityType}-publish-success`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full ring-1 bg-emerald-400/15 ring-emerald-400/30">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" strokeWidth={2.5} />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-200">{cfg.badge}</span>
          </div>
          <h2 className="text-white text-2xl sm:text-3xl font-bold tracking-tight leading-tight">
            Gotowe, {firstName}! 🎉
          </h2>
          <p className="text-white/70 text-sm sm:text-base leading-relaxed">
            {cfg.publishedText}
          </p>

          {/* Link do strony */}
          {slug && (
            <div className="p-4 rounded-2xl bg-white/[0.04] ring-1 ring-white/10" data-testid={`${entityType}-success-link-box`}>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--apisai-primary)] mb-1">Link do Twojej strony</p>
              <p className="text-white text-sm font-medium truncate">apisai.pl/{cfg.publicPrefix}/{slug}</p>
            </div>
          )}

          {/* Przyciski udostępniania */}
          {slug && (
            <div className="grid grid-cols-2 gap-2" data-testid={`${entityType}-success-share`}>
              <a
                href={`/${cfg.publicPrefix}/${slug}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${entityType}-success-view-link`}
                className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl bg-[var(--apisai-primary)] hover:bg-[var(--apisai-primary-600)] text-white text-sm font-semibold transition-all min-h-[48px]"
              >
                <Globe className="w-4 h-4" /> Zobacz stronę
              </a>
              <button
                onClick={copyLink}
                data-testid={`${entityType}-success-copy-link`}
                className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl bg-white/[0.06] hover:bg-white/[0.12] text-white text-sm font-semibold transition-all min-h-[48px] ring-1 ring-white/10"
              >
                <Copy className="w-4 h-4" /> Skopiuj link
              </button>
              <button
                onClick={shareFacebook}
                data-testid={`${entityType}-success-share-fb`}
                className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl bg-[#1877F2]/90 hover:bg-[#1877F2] text-white text-sm font-semibold transition-all min-h-[48px]"
              >
                <Facebook className="w-4 h-4" /> Facebook
              </button>
              <button
                onClick={shareWhatsApp}
                data-testid={`${entityType}-success-share-wa`}
                className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl bg-[#25D366]/90 hover:bg-[#25D366] text-white text-sm font-semibold transition-all min-h-[48px]"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </button>
            </div>
          )}

          {/* ───── Gotowy post na Facebooka: podgląd + kopiowanie treści ───── */}
          {slug && (
            <div className="space-y-3" data-testid={`${entityType}-success-fbpost`}>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--apisai-primary)]">Gotowy post na Facebooka</p>

              {/* Podgląd posta — wygląd zbliżony do FB */}
              <div className="rounded-2xl bg-white overflow-hidden ring-1 ring-white/10 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.6)]" data-testid={`${entityType}-fbpost-preview`}>
                <div className="p-3.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-full bg-[var(--apisai-primary)] flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                      {(entityName || 'A').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[#050505] text-[13px] font-semibold leading-tight truncate">{entityName || 'Twój profil'}</p>
                      <p className="text-[#65676B] text-[11px] flex items-center gap-1">Teraz · <Globe className="w-3 h-3" /></p>
                    </div>
                  </div>
                  <p className="text-[#050505] text-[13px] leading-relaxed mt-2.5 whitespace-pre-line">{postText}</p>
                </div>

                {/* Karta podglądu linku (jak generuje FB) */}
                <div className="bg-[#F0F2F5] border-t border-[#E4E6EB]">
                  {coverImage ? (
                    <img src={coverImage} alt="" className="w-full h-32 object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-20 bg-gradient-to-br from-[#04202F] to-[#073447] flex items-center justify-center">
                      <Globe className="w-6 h-6 text-white/40" />
                    </div>
                  )}
                  <div className="px-3.5 py-2.5">
                    <p className="text-[#65676B] text-[10px] uppercase tracking-wide">apisai.pl</p>
                    <p className="text-[#050505] text-[13px] font-semibold leading-tight truncate">{entityName || 'Twoja strona w APISAI'}</p>
                  </div>
                </div>

                {/* Pasek akcji FB (dekoracyjny) */}
                <div className="flex items-center justify-around border-t border-[#E4E6EB] py-1.5 text-[#65676B]">
                  <span className="flex items-center gap-1.5 text-[12px] font-medium"><ThumbsUp className="w-3.5 h-3.5" /> Lubię to!</span>
                  <span className="flex items-center gap-1.5 text-[12px] font-medium"><MessageSquare className="w-3.5 h-3.5" /> Komentarz</span>
                  <span className="flex items-center gap-1.5 text-[12px] font-medium"><Share2 className="w-3.5 h-3.5" /> Udostępnij</span>
                </div>
              </div>

              {/* Kopiuj treść posta */}
              <button
                onClick={copyPost}
                data-testid={`${entityType}-success-copy-post`}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[var(--apisai-primary)] hover:bg-[var(--apisai-primary-600)] text-white text-sm font-semibold transition-all min-h-[48px]"
              >
                {copied ? <ClipboardCheck className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Skopiowano treść posta' : 'Kopiuj treść posta'}
              </button>
              <p className="text-white/45 text-xs text-center leading-snug">
                Skopiuj treść, kliknij „Facebook" powyżej i wklej post — link doda podgląd strony automatycznie.
              </p>
            </div>
          )}

          {/* Lista wartości — co użytkownik właśnie zyskał */}
          <div className="space-y-2 p-3.5 rounded-2xl bg-emerald-400/10 ring-1 ring-emerald-400/20" data-testid={`${entityType}-success-checklist`}>
            {cfg.checklist.map((t) => (
              <div key={t} className="flex items-center gap-2.5 text-sm text-emerald-50">
                <CheckCircle2 className="w-4 h-4 text-emerald-300 flex-shrink-0" strokeWidth={2.5} />
                <span>{t}</span>
              </div>
            ))}
          </div>

          {/* Zobacz swoją kartę w katalogu APISAI */}
          <a
            href={cfg.catalogHref}
            data-testid={`${entityType}-success-catalog-card`}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/[0.06] hover:bg-white/[0.12] text-white text-sm font-semibold transition-all min-h-[48px] ring-1 ring-white/10"
          >
            <LayoutGrid className="w-4 h-4 text-[var(--apisai-primary)]" />
            Zobacz swoją kartę w katalogu APISAI
          </a>

          {/* Co dalej? */}
          {nextItems.length > 0 && (
            <>
              <div className="h-px bg-white/8" />
              <div className="space-y-3">
                <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[var(--apisai-primary)]">Co dalej?</p>
                <div className="space-y-2">
                  {nextItems.map(({ key, icon: Icon, label, path, action }) => (
                    <button
                      key={key}
                      onClick={() => (action ? action() : onNavigate?.(path))}
                      data-testid={`${entityType}-success-next-${key}`}
                      className="group w-full flex items-center gap-3 p-3.5 rounded-2xl bg-white/[0.04] hover:bg-white/[0.09] ring-1 ring-white/10 hover:ring-[var(--apisai-primary)]/50 transition-all text-left"
                    >
                      <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-[var(--apisai-primary)]/15">
                        <Icon className="w-4 h-4 text-[var(--apisai-primary)]" />
                      </span>
                      <span className="flex-1 text-white text-sm font-semibold">{label}</span>
                      <ArrowRight className="w-4 h-4 text-white/45 group-hover:text-[var(--apisai-primary)] flex-shrink-0 transition-colors" />
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </Shell>
    </>
  );
}
